import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiTrendingUp } from "react-icons/fi";

// V4 Components
import { ContentHeader, StatCard, LineChart } from "../../../components/v4";

// Components
import ForecastVariablesPanel from "../../components/ForecastVariablesPanel";
import CurrentMonthTracker from "../../components/CurrentMonthTracker";
import BudgetDataTable from "../../components/BudgetDataTable";
import BusinessTakehomeTable from "../../components/BusinessTakehomeTable";

// Shared hook + constants
import useBudgetForecast, { budgetConfig, businessExpensesDefault, taxRate, monthlyGrossEstimate } from "../../hooks/useBudgetForecast";

const STORAGE_KEY_SECTIONS = "budgetForecast_collapsedSections";
const STORAGE_KEY_CATEGORIES = "budgetForecast_collapsedCategories";
const STORAGE_KEY_EST_MONTHLY_GROSS = "budgetForecast_estimatedMonthlyGross";
const STORAGE_KEY_VARIABLE_ITEMS = "budgetForecast_variableItems";
const STORAGE_KEY_VARIABLE_VALUES = "budgetForecast_variableValues";

const BudgetForecast = () => {
	// Refs for synchronized scrolling
	const budgetTableRef = useRef(null);
	const takehomeTableRef = useRef(null);
	const isScrolling = useRef(false);

	// Synchronized scroll handler
	const handleScroll = useCallback((source) => {
		if (isScrolling.current) return;
		isScrolling.current = true;

		const sourceRef = source === "budget" ? budgetTableRef : takehomeTableRef;
		const targetRef = source === "budget" ? takehomeTableRef : budgetTableRef;

		if (sourceRef.current && targetRef.current) {
			targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
		}

		requestAnimationFrame(() => {
			isScrolling.current = false;
		});
	}, []);

	// UI state
	const [years, setYears] = useState(1);
	const [showPreviousYear, setShowPreviousYear] = useState(false);
	const [collapsedSections, setCollapsedSections] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_SECTIONS);
			return saved ? JSON.parse(saved) : {};
		} catch {
			return {};
		}
	});
	const [collapsedCategories, setCollapsedCategories] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_CATEGORIES);
			return saved ? JSON.parse(saved) : {};
		} catch {
			return {};
		}
	});
	const [estimatedMonthlyGross, setEstimatedMonthlyGross] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_EST_MONTHLY_GROSS);
			return saved ? parseFloat(saved) : monthlyGrossEstimate;
		} catch {
			return monthlyGrossEstimate;
		}
	});
	const [variableItems, setVariableItems] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_VARIABLE_ITEMS);
			return saved ? new Set(JSON.parse(saved)) : new Set();
		} catch {
			return new Set();
		}
	});
	const [variableValues, setVariableValues] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_VARIABLE_VALUES);
			return saved ? JSON.parse(saved) : {};
		} catch {
			return {};
		}
	});

	// Persist state to localStorage
	useEffect(() => {
		localStorage.setItem(STORAGE_KEY_SECTIONS, JSON.stringify(collapsedSections));
	}, [collapsedSections]);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(collapsedCategories));
	}, [collapsedCategories]);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY_EST_MONTHLY_GROSS, String(estimatedMonthlyGross));
	}, [estimatedMonthlyGross]);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY_VARIABLE_ITEMS, JSON.stringify([...variableItems]));
	}, [variableItems]);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY_VARIABLE_VALUES, JSON.stringify(variableValues));
	}, [variableValues]);

	// Variable item handlers
	const toggleVariableItem = (itemId) => {
		setVariableItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
				setVariableValues((vals) => {
					const newVals = { ...vals };
					delete newVals[itemId];
					return newVals;
				});
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const setVariableValue = (itemId, value) => {
		setVariableValues((prev) => ({
			...prev,
			[itemId]: parseFloat(value) || 0,
		}));
	};

	// Shared hook — all data fetching and calculations
	const {
		forecastData,
		loading,
		error,
		groupedItems,
		invoicesByMonth,
		historicalIncomeByMonth,
		historicalExpensesByMonth,
		networthByMonth,
		actualSpendingByMonth,
		overdueAmount,
		eomEstimate,
		yearEndBalance,
		startingBalance,
		lineChartData,
		getEffectiveGross,
		isUsingEstimate,
		getBusinessExpenses,
		getCalculatedTotals,
		isMonthClosed,
		isCurrentMonth,
		shouldShowActuals,
		getLiveItemData,
		getEffectiveItemValue,
		getMonthProgress,
		getTrackedBudgetItems,
		getTrackerTotals,
	} = useBudgetForecast({
		years,
		showPreviousYear,
		estimatedMonthlyGross,
		variableItems,
		variableValues,
	});

	// Toggle handlers
	const toggleSection = (section) => {
		setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
	};

	const toggleCategory = (sectionCategory) => {
		setCollapsedCategories((prev) => ({ ...prev, [sectionCategory]: !prev[sectionCategory] }));
	};

	// Format helpers
	const formatCurrency = (value) => {
		if (value === 0) return "—";
		const absValue = Math.abs(value);
		const formatted = absValue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
		return value < 0 ? `-$${formatted}` : `$${formatted}`;
	};

	const formatCompact = (value) => {
		const absValue = Math.abs(value);
		const sign = value < 0 ? "-" : "";
		if (absValue >= 1000000) return `${sign}$${(absValue / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
		if (absValue >= 1000) return `${sign}$${(absValue / 1000).toFixed(1).replace(/\.0$/, "")}k`;
		return `${sign}$${absValue}`;
	};

	// Current month tracker data
	const trackedItems = getTrackedBudgetItems();
	const monthProgress = getMonthProgress() * 100;
	const currentMonthName = new Date().toLocaleString("default", { month: "long", year: "numeric" });

	// Loading state
	if (loading) {
		return (
			<div className="budget-forecast">
				<div className="budget-forecast__loading">Loading forecast...</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="budget-forecast">
				<div className="budget-forecast__error">{error}</div>
			</div>
		);
	}

	const { months } = forecastData;

	// Year controls component
	const YearControls = (
		<div className="budget-forecast__year-controls">
			<button className={`budget-forecast__prev-year-toggle ${showPreviousYear ? "active" : ""}`} onClick={() => setShowPreviousYear(!showPreviousYear)}>
				{showPreviousYear ? "−" : "+"} Prev
			</button>
			<div className="budget-forecast__year-toggle">
				{[1, 3, 5].map((y) => (
					<button key={y} className={years === y ? "active" : ""} onClick={() => setYears(y)}>
						{y}Y
					</button>
				))}
			</div>
		</div>
	);

	return (
		<div className="budget-forecast">
			<ContentHeader icon={<FiTrendingUp />} title="Budget Forecast" subtitle={`${years} Year Projection`} metadata={`Starting: ${formatCurrency(startingBalance)}`} actions={YearControls} />

			<div className="v4-stats-row">
				<StatCard label="Starting Balance" value={Math.round(startingBalance)} prefix="$" animate={false} />
				<StatCard label="EOM Estimate" value={Math.round(eomEstimate)} prefix="$" animate={false} />
				<StatCard label="Year End Projection" value={Math.round(yearEndBalance)} prefix="$" animate={false} />
			</div>

			<ForecastVariablesPanel
				estimatedMonthlyGross={estimatedMonthlyGross}
				onEstimatedMonthlyGrossChange={setEstimatedMonthlyGross}
				variableCount={variableItems.size}
				onClearVariables={() => {
					setVariableItems(new Set());
					setVariableValues({});
				}}
			/>

			{/*<CurrentMonthTracker trackedItems={trackedItems} monthProgress={monthProgress} currentMonthName={currentMonthName} formatCurrency={formatCurrency} getTrackerTotals={getTrackerTotals} />*/}

			<LineChart data={lineChartData} title="Running Balance" subtitle="(includes Business Takehome)" formatValue={formatCurrency} formatCompact={formatCompact} showGrid showYAxis showTooltip showLabels />

			<BudgetDataTable
				months={months}
				groupedItems={groupedItems}
				collapsedSections={collapsedSections}
				collapsedCategories={collapsedCategories}
				toggleSection={toggleSection}
				toggleCategory={toggleCategory}
				variableItems={variableItems}
				variableValues={variableValues}
				toggleVariableItem={toggleVariableItem}
				setVariableValue={setVariableValue}
				getEffectiveItemValue={getEffectiveItemValue}
				getEffectiveGross={getEffectiveGross}
				isUsingEstimate={isUsingEstimate}
				getCalculatedTotals={getCalculatedTotals}
				startingBalance={startingBalance}
				businessExpenses={businessExpensesDefault}
				getBusinessExpenses={getBusinessExpenses}
				taxRate={taxRate}
				formatCurrency={formatCurrency}
				scrollRef={budgetTableRef}
				onScroll={() => handleScroll("budget")}
				actualSpendingByMonth={actualSpendingByMonth}
				isMonthClosed={isMonthClosed}
				isCurrentMonth={isCurrentMonth}
				shouldShowActuals={shouldShowActuals}
				historicalIncomeByMonth={historicalIncomeByMonth}
				historicalExpensesByMonth={historicalExpensesByMonth}
				invoicesByMonth={invoicesByMonth}
				monthProgress={getMonthProgress()}
				getLiveItemData={getLiveItemData}
				networthByMonth={networthByMonth}
			/>

			<BusinessTakehomeTable months={months} invoicesByMonth={invoicesByMonth} historicalIncomeByMonth={historicalIncomeByMonth} historicalExpensesByMonth={historicalExpensesByMonth} historicalCutoff={budgetConfig.historicalCutoff} businessExpenses={businessExpensesDefault} taxRate={taxRate} formatCurrency={formatCurrency} scrollRef={takehomeTableRef} onScroll={() => handleScroll("takehome")} forecastItems={forecastData?.items || []} isCurrentMonth={isCurrentMonth} monthProgress={getMonthProgress()} getLiveItemData={getLiveItemData} getEffectiveItemValue={getEffectiveItemValue} overdueAmount={overdueAmount} />
		</div>
	);
};

export default BudgetForecast;
